/**
 * CTE Build Planner
 * AI-assisted build planning with material suitability, orientation sweep,
 * risk assessment, and comprehensive build plan generation.
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  integrateCTE, rotateCTETensor, buildAngleToEuler,
  computeBaseRef, getDispMagnitude, exportBinarySTL,
  meshExtents,
} from './cte-utils';
import { STLViewer, ColorBar, BuildAngleGizmo, useCadModelUpload } from './cte-shared';
import { generateBuildPlanPDF } from './cte-build-plan-pdf';
import './CTEBuildPlanner.css';
import { authFetch } from '../../utils/api';

const SWEEP_ANGLES = [0, 15, 30, 45, 60, 90];

function markdownToHTML(md) {
  let html = md
    .replace(/#### (.+)/g, '<h4>$1</h4>')
    .replace(/### (.+)/g, '<h3>$1</h3>')
    .replace(/## (.+)/g, '<h2>$1</h2>')
    .replace(/# (.+)/g, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n{2,}/g, '\n\n');

  const blocks = html.split('\n\n');
  const result = [];
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('<h')) { result.push(trimmed); continue; }
    const lines = trimmed.split('\n');
    const isList = lines.every(l => /^[-*]\s/.test(l.trim()));
    const isNumbered = lines.every(l => /^\d+\.\s/.test(l.trim()));
    if (isList) {
      result.push(`<ul>${lines.map(l => `<li>${l.replace(/^[-*]\s+/, '').trim()}</li>`).join('')}</ul>`);
    } else if (isNumbered) {
      result.push(`<ol>${lines.map(l => `<li>${l.replace(/^\d+\.\s+/, '').trim()}</li>`).join('')}</ol>`);
    } else {
      result.push(`<p>${trimmed.replace(/\n/g, '<br/>')}</p>`);
    }
  }
  return result.join('');
}

function assessRisk(mouldTg, partCureTemp) {
  if (mouldTg === null) return { level: 'green', label: 'OK', note: 'No Tg limit' };
  const headroom = mouldTg - partCureTemp;
  if (headroom < 0) return { level: 'red', label: 'HIGH RISK', note: `Tg ${Math.abs(headroom)} deg below cure` };
  if (headroom < 20) return { level: 'amber', label: 'CAUTION', note: `Only ${headroom} deg headroom` };
  return { level: 'green', label: 'OK', note: `${headroom} deg headroom` };
}

function runOrientationSweep(mouldMat, positions, t1, t2) {
  const nv = positions.length / 3;
  const base = computeBaseRef(positions);

  return SWEEP_ANGLES.map(angle => {
    const orient = buildAngleToEuler(angle, 0);
    const ax = integrateCTE(mouldMat, 'ax', t1, t2) * 1e-6;
    const ay = integrateCTE(mouldMat, 'ay', t1, t2) * 1e-6;
    const az = integrateCTE(mouldMat, 'az', t1, t2) * 1e-6;
    const tensor = rotateCTETensor(ax, ay, az, orient.rx, orient.ry, orient.rz);
    const [e00, e01, e02, e10, e11, e12, e20, e21, e22] = tensor.flat();

    let maxM = 0;
    for (let i = 0; i < nv; i++) {
      const px = positions[i * 3] - base.x;
      const py = positions[i * 3 + 1] - base.y;
      const pz = positions[i * 3 + 2] - base.z;
      const dx = e00 * px + e01 * py + e02 * pz;
      const dy = e10 * px + e11 * py + e12 * pz;
      const dz = e20 * px + e21 * py + e22 * pz;
      const m = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (m > maxM) maxM = m;
    }
    return { angle, maxDispMM: maxM };
  });
}


export default function CTEBuildPlanner({ materials }) {
  const mouldMaterials = materials.filter(m => m.category === 'mould');
  const partMaterials = materials.filter(m => m.category === 'layup');

  const [mouldType, setMouldType] = useState('female');
  const [mouldMatId, setMouldMatId] = useState('');
  const [partMatId, setPartMatId] = useState(partMaterials[0]?.id || '');
  const [dims, setDims] = useState({ x: 1000, y: 500, z: 10 });
  const { model, setModel, loadFile } = useCadModelUpload();
  const positions = model.positions;
  const fileName = model.fileName;
  const fileError = model.error;
  const loadingFile = model.loading;
  const rawFileBytes = model.rawBytes;
  const fileIsStep = model.isStep;
  const setFileError = (message) => setModel((m) => ({ ...m, error: message }));

  const [sweepResults, setSweepResults] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [buildAzimuth, setBuildAzimuth] = useState(0);

  const [aiPlan, setAiPlan] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const [compResult, setCompResult] = useState(null);
  const [showOriginal, setShowOriginal] = useState(true);
  const [defScale, setDefScale] = useState(1);
  const [stepExporting, setStepExporting] = useState(false);

  const fileRef = useRef();

  const mouldMat = materials.find(m => m.id === mouldMatId);
  const partMat = materials.find(m => m.id === partMatId);
  const partCureTemp = partMat?.cureTemp || null;

  // --- Material suitability table ---
  const suitability = useMemo(() => {
    if (!partCureTemp) return null;
    return mouldMaterials.map(m => {
      const risk = assessRisk(m.cureTemp, partCureTemp);
      const zStrain = integrateCTE(m, 'az', 23, partCureTemp);
      return {
        id: m.id,
        name: m.name,
        tg: m.cureTemp,
        riskLevel: risk.level,
        riskLabel: risk.label,
        riskNote: risk.note,
        zStrainPerM: zStrain.toFixed(1),
      };
    });
  }, [mouldMaterials, partCureTemp]);

  useEffect(() => {
    if (!positions) return;
    const ext = meshExtents(positions);
    if (!ext) return;
    setDims({
      x: Math.round(ext.x * 100) / 100,
      y: Math.round(ext.y * 100) / 100,
      z: Math.round(ext.z * 100) / 100,
    });
  }, [positions]);

  const handleFile = useCallback(async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setSweepResults(null);
    setCompResult(null);
    setSelectedAngle(null);
    await loadFile(file);
  }, [loadFile]);

  // --- Orientation sweep ---
  const handleSweep = useCallback(() => {
    if (!mouldMat || !positions || !partCureTemp) return;
    setSweeping(true);
    setTimeout(() => {
      const results = runOrientationSweep(mouldMat, positions, 23, partCureTemp);
      setSweepResults(results);
      setSweeping(false);
    }, 50);
  }, [mouldMat, positions, partCureTemp]);

  // --- Compensation at a given build angle + azimuth ---
  const computeCompensation = useCallback((angle, azimuth = 0) => {
    if (!mouldMat || !positions || !partCureTemp) return null;
    const orient = buildAngleToEuler(angle, azimuth);
    const axStrain = integrateCTE(mouldMat, 'ax', 23, partCureTemp) * 1e-6;
    const ayStrain = integrateCTE(mouldMat, 'ay', 23, partCureTemp) * 1e-6;
    const azStrain = integrateCTE(mouldMat, 'az', 23, partCureTemp) * 1e-6;
    const tensor = rotateCTETensor(axStrain, ayStrain, azStrain, orient.rx, orient.ry, orient.rz);
    const [e00, e01, e02, e10, e11, e12, e20, e21, e22] = tensor.flat();

    const nv = positions.length / 3;
    const base = computeBaseRef(positions);
    const compensated = new Float32Array(positions.length);
    let maxCompMM = 0;
    let totalCompMM = 0;

    for (let i = 0; i < nv; i++) {
      const px = positions[i * 3] - base.x;
      const py = positions[i * 3 + 1] - base.y;
      const pz = positions[i * 3 + 2] - base.z;
      const dx = e00 * px + e01 * py + e02 * pz;
      const dy = e10 * px + e11 * py + e12 * pz;
      const dz = e20 * px + e21 * py + e22 * pz;
      compensated[i * 3]     = positions[i * 3]     - dx;
      compensated[i * 3 + 1] = positions[i * 3 + 1] - dy;
      compensated[i * 3 + 2] = positions[i * 3 + 2] - dz;
      const mag = getDispMagnitude(dx, dy, dz);
      if (mag > maxCompMM) maxCompMM = mag;
      totalCompMM += mag;
    }

    const negTensor = tensor.map(row => row.map(v => -v));
    return {
      compensatedPositions: compensated,
      strainTensor: negTensor,
      tensor,
      base,
      maxCompMM,
      avgCompMM: totalCompMM / nv,
      nTriangles: nv / 3,
      deltaT: partCureTemp - 23,
      matName: mouldMat.name,
      buildAngle: angle,
      orient,
      strainDiag: {
        x: tensor[0][0] * 1e6,
        y: tensor[1][1] * 1e6,
        z: tensor[2][2] * 1e6,
      },
    };
  }, [mouldMat, positions, partCureTemp]);

  // Auto-select best angle when sweep completes
  useEffect(() => {
    if (!sweepResults) return;
    const best = sweepResults.reduce((b, r) => r.maxDispMM < b.maxDispMM ? r : b, sweepResults[0]);
    setSelectedAngle(best.angle);
  }, [sweepResults]);

  // Re-run compensation whenever selected angle or azimuth changes
  useEffect(() => {
    if (selectedAngle === null || !positions) { setCompResult(null); return; }
    const result = computeCompensation(selectedAngle, buildAzimuth);
    setCompResult(result);
  }, [selectedAngle, buildAzimuth, computeCompensation, positions]);

  // --- AI build plan ---
  const generatePlan = useCallback(async () => {
    if (!mouldMat || !partMat) return;
    setAiLoading(true);
    setAiError(null);
    setAiPlan(null);

    const mouldRisk = suitability?.find(s => s.id === mouldMatId);

    const payload = {
      mouldType,
      mouldMaterial: { name: mouldMat.name, tg: mouldMat.cureTemp },
      partMaterial: { name: partMat.name, cureTemp: partMat.cureTemp },
      dims,
      hasSTL: !!positions,
      triangles: positions ? (positions.length / 9) : 0,
      suitability: mouldRisk || null,
      sweepResults: sweepResults || null,
      selectedAngle: selectedAngle,
      buildAzimuth: buildAzimuth,
      compResult: compResult ? {
        buildAngle: compResult.buildAngle,
        maxCompMM: compResult.maxCompMM.toFixed(4),
        avgCompMM: compResult.avgCompMM.toFixed(4),
        deltaT: compResult.deltaT.toFixed(1),
        strainX: compResult.strainDiag.x.toFixed(2),
        strainY: compResult.strainDiag.y.toFixed(2),
        strainZ: compResult.strainDiag.z.toFixed(2),
      } : null,
    };

    try {
      const res = await fetch('/api/cte/ai/build-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planData: payload }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Unknown error');
      setAiPlan(data.plan);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  }, [mouldType, mouldMat, partMat, dims, positions, suitability, mouldMatId, sweepResults, selectedAngle, buildAzimuth, compResult]);

  const handleGizmoAngleChange = useCallback((angle, azimuth) => {
    setSelectedAngle(angle);
    setBuildAzimuth(azimuth);
  }, []);

  // --- Export functions ---
  const DOWNLOAD_DISCLAIMER =
    'DISCLAIMER\n\n' +
    'Rapid Fusion accepts no responsibility for the accuracy of these results. ' +
    'This tool is provided as a computational aid only.\n\n' +
    'A qualified engineer must review and verify all compensated geometry before manufacturing.\n\n' +
    'Do you wish to proceed with the download?';

  const downloadSTL = useCallback(() => {
    if (!compResult) return;
    if (!window.confirm(DOWNLOAD_DISCLAIMER)) return;
    const buffer = exportBinarySTL(compResult.compensatedPositions);
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const baseName = fileName ? fileName.replace(/\.[^.]+$/, '') : 'mould';
    a.href = url;
    a.download = `${baseName}_compensated.stl`;
    a.click();
    URL.revokeObjectURL(url);
  }, [compResult, fileName]);

  const downloadSTEP = useCallback(async () => {
    if (!compResult || !rawFileBytes || !fileIsStep) return;
    if (!window.confirm(DOWNLOAD_DISCLAIMER)) return;
    setStepExporting(true);
    setFileError(null);

    try {
      const tensor = compResult.tensor;
      const base = compResult.base;
      const matrix = [
        [1 - tensor[0][0], -tensor[0][1], -tensor[0][2]],
        [-tensor[1][0], 1 - tensor[1][1], -tensor[1][2]],
        [-tensor[2][0], -tensor[2][1], 1 - tensor[2][2]],
      ];
      const translation = [
        tensor[0][0] * base.x + tensor[0][1] * base.y + tensor[0][2] * base.z,
        tensor[1][0] * base.x + tensor[1][1] * base.y + tensor[1][2] * base.z,
        tensor[2][0] * base.x + tensor[2][1] * base.y + tensor[2][2] * base.z,
      ];

      const formData = new FormData();
      const blob = new Blob([rawFileBytes], { type: 'application/step' });
      formData.append('stepFile', blob, fileName);
      formData.append('matrix', JSON.stringify(matrix));
      formData.append('translation', JSON.stringify(translation));

      const submitRes = await authFetch('/api/cte/compensate-step', {
        method: 'POST',
        body: formData,
      });
      if (!submitRes.ok) {
        const errData = await submitRes.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${submitRes.status}`);
      }

      const { jobId } = await submitRes.json();

      const poll = async () => {
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          const statusRes = await authFetch(`/api/cte/compensate-step/status/${jobId}`);
          if (!statusRes.ok) throw new Error('Lost connection to export job');
          const { status, error: jobError } = await statusRes.json();
          if (status === 'error') throw new Error(jobError || 'STEP processing failed on server');
          if (status === 'done') return;
        }
      };
      await poll();

      const dlRes = await authFetch(`/api/cte/compensate-step/download/${jobId}`);
      if (!dlRes.ok) throw new Error('Download failed — file may have expired');

      const resultBlob = await dlRes.blob();
      const url = URL.createObjectURL(resultBlob);
      const a = document.createElement('a');
      const baseName = fileName ? fileName.replace(/\.[^.]+$/, '') : 'mould';
      a.href = url;
      a.download = `${baseName}_compensated.step`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFileError('STEP export failed: ' + err.message);
    } finally {
      setStepExporting(false);
    }
  }, [compResult, rawFileBytes, fileIsStep, fileName]);

  // --- Sweep bar helpers ---
  const bestSweepAngle = sweepResults
    ? sweepResults.reduce((best, r) => r.maxDispMM < best.maxDispMM ? r : best, sweepResults[0]).angle
    : null;
  const maxSweepDisp = sweepResults
    ? Math.max(...sweepResults.map(r => r.maxDispMM))
    : 0;

  // --- PDF export ---
  const handleExportPDF = useCallback(() => {
    let viewerImage = null;
    let gizmoImage = null;

    try {
      const viewerCanvas = document.querySelector('.cte-viewer-wrap canvas');
      if (viewerCanvas) viewerImage = viewerCanvas.toDataURL('image/png');
    } catch (_) { /* canvas may be tainted */ }

    try {
      const gizmoCanvas = document.querySelector('.cte-gizmo-viewport canvas');
      if (gizmoCanvas) gizmoImage = gizmoCanvas.toDataURL('image/png');
    } catch (_) { /* canvas may be tainted */ }

    generateBuildPlanPDF({
      partMaterial: partMat?.name || '',
      partCureTemp: partCureTemp,
      mouldMaterial: mouldMat?.name || '',
      mouldTg: mouldMat?.cureTemp,
      mouldType,
      dims,
      fileName: fileName || null,
      suitability: suitability?.find(s => s.id === mouldMatId) || null,
      sweepResults: sweepResults || null,
      bestAngle: bestSweepAngle,
      selectedAngle,
      compResult: compResult || null,
      aiPlan: aiPlan || null,
      viewerImage,
      gizmoImage,
    });
  }, [partMat, partCureTemp, mouldMat, mouldType, dims, fileName, suitability, mouldMatId, sweepResults, bestSweepAngle, selectedAngle, compResult, aiPlan]);

  const selectedSuitability = suitability?.find(s => s.id === mouldMatId) || null;

  return (
    <div style={{ maxWidth: 1000 }}>
      {loadingFile && (
        <div className="cte-loading-overlay">
          <div className="cte-loading-modal">
            <div className="cte-loading-spinner" />
            <div className="cte-loading-title">Parsing CAD File</div>
            <div className="cte-loading-sub">{fileName || 'Loading...'}</div>
            <div className="cte-loading-hint">STEP files require the geometry engine to load. This can take a moment.</div>
          </div>
        </div>
      )}

      <div className="cte-info-box" style={{ marginBottom: 16 }}>
        Upload your CAD model, define the part and mould, then generate an AI build plan
        covering material suitability, print orientation, cure cycle, and compensation.
      </div>

      {/* ─── STEP 1: UPLOAD CAD ─────────────────────────────────────── */}
      <div className="cte-card" style={{ marginBottom: 16 }}>
        <div className="cte-card-header">
          <span>Step 1 — Upload CAD Model</span>
          <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
            Dimensions are read from the file automatically
          </span>
        </div>
        <div className="cte-card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input ref={fileRef} type="file" accept=".stl,.obj,.step,.stp" onChange={handleFile} style={{ display: 'none' }} />
            <button className="cte-btn cte-btn-primary" onClick={() => fileRef.current.click()} disabled={loadingFile}>
              {loadingFile ? 'Parsing...' : 'Upload STL / OBJ / STEP'}
            </button>
            {fileName && (
              <span style={{ fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-secondary)' }}>
                {fileName}
                {positions && (
                  <span style={{ color: 'var(--rf-success)', marginLeft: 10 }}>
                    {(positions.length / 9).toLocaleString()} triangles
                  </span>
                )}
              </span>
            )}
          </div>
          {fileError && (
            <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)', marginTop: 10 }}>{fileError}</div>
          )}
          {!positions && !loadingFile && (
            <div style={{ padding: '16px 0 4px', color: 'var(--rf-text-muted)', fontSize: 'var(--rf-font-size-sm)' }}>
              Optional — you can also enter dimensions manually in Step 2.
              Uploading a model enables the orientation sweep in Step 4.
              The file stays loaded when you change tabs.
            </div>
          )}
          {positions && (
            <div style={{ marginTop: 12 }}>
              <div className="cte-section-label" style={{ marginBottom: 8 }}>
                Bounding Box from CAD (mm)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {[['x', 'X'], ['y', 'Y'], ['z', 'Z']].map(([k, label]) => (
                  <div key={k} style={{ padding: '8px 12px', background: 'var(--rf-bg-tertiary)', borderRadius: 6, textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-primary)', fontWeight: 600 }}>{dims[k]} mm</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── STEP 2: PART DEFINITION ───────────────────────────────── */}
      <div className="cte-card" style={{ marginBottom: 16 }}>
        <div className="cte-card-header">
          <span>Step 2 — Define Your Part</span>
          <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
            Cure temperature drives all calculations below
          </span>
        </div>
        <div className="cte-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div className="cte-form-group" style={{ marginBottom: 12 }}>
                <label>Part Material (curing inside mould)</label>
                <select value={partMatId} onChange={e => { setPartMatId(e.target.value); setMouldMatId(''); setSweepResults(null); setSelectedAngle(null); setCompResult(null); setAiPlan(null); }}>
                  {partMaterials.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.name} — Cure {m.cureTemp} deg C
                    </option>
                  ))}
                </select>
              </div>
              <div className="cte-form-group">
                <label>Mould Type</label>
                <div className="cte-planner-toggle">
                  <button className={mouldType === 'female' ? 'active' : ''} onClick={() => setMouldType('female')}>
                    Female (cavity)
                  </button>
                  <button className={mouldType === 'male' ? 'active' : ''} onClick={() => setMouldType('male')}>
                    Male (plug)
                  </button>
                </div>
              </div>
            </div>
            {!positions && (
              <div>
                <div className="cte-section-label" style={{ marginBottom: 8 }}>
                  Part Dimensions (mm) — or upload a CAD file in Step 1
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  {[['x', 'X Length'], ['y', 'Y Width'], ['z', 'Z Thickness']].map(([k, label]) => (
                    <div className="cte-form-group" key={k}>
                      <label>{label}</label>
                      <input type="number" value={dims[k]} onChange={e => setDims(d => ({ ...d, [k]: +e.target.value }))} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── STEP 3: MOULD MATERIAL SELECTION (SUITABILITY TABLE) ── */}
      {suitability && suitability.length > 0 && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <span>Step 3 — Choose Mould Material for {partCureTemp} deg C Cure</span>
          </div>
          <div className="cte-card-body">
            {!mouldMatId && (
              <div className="cte-info-box" style={{ marginBottom: 12 }}>
                Select a mould material below. Materials are ranked by suitability for your part cure temperature.
              </div>
            )}
            {mouldMatId && mouldMat && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '8px 12px', background: 'rgba(var(--rf-primary-rgb), 0.08)', borderRadius: 6 }}>
                <span style={{ fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-primary)', fontWeight: 600 }}>
                  Selected: {mouldMat.name}
                </span>
                {selectedSuitability && (
                  <span className={`cte-risk-badge ${selectedSuitability.riskLevel}`}>
                    <span className={`cte-risk-dot ${selectedSuitability.riskLevel}`} />
                    {selectedSuitability.riskLabel}
                  </span>
                )}
                {mouldMat.cureTemp && (
                  <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
                    Tg {mouldMat.cureTemp} deg C
                  </span>
                )}
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table className="cte-suitability-table">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Tg</th>
                    <th>Risk</th>
                    <th>Z-Strain (um/m)</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {suitability.map(s => (
                    <tr
                      key={s.id}
                      className={`clickable ${s.id === mouldMatId ? 'selected' : ''}`}
                      onClick={() => { setMouldMatId(s.id); setSweepResults(null); setSelectedAngle(null); setCompResult(null); setAiPlan(null); }}
                    >
                      <td style={{ fontWeight: s.id === mouldMatId ? 600 : 400 }}>{s.name}</td>
                      <td>{s.tg !== null ? `${s.tg} deg C` : '---'}</td>
                      <td>
                        <span className={`cte-risk-badge ${s.riskLevel}`}>
                          <span className={`cte-risk-dot ${s.riskLevel}`} />
                          {s.riskLabel}
                        </span>
                      </td>
                      <td>{s.zStrainPerM}</td>
                      <td style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>{s.riskNote}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── STEP 4: ORIENTATION SWEEP ─────────────────────────────── */}
      {mouldMatId && positions && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <span>Step 4 — Orientation Sweep</span>
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
              Find the best print angle for your geometry
            </span>
          </div>
          <div className="cte-card-body">
            {!sweepResults && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="cte-btn cte-btn-primary"
                  onClick={handleSweep}
                  disabled={sweeping}
                >
                  {sweeping ? 'Sweeping...' : 'Run Orientation Sweep'}
                </button>
                <span style={{ fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-muted)' }}>
                  Tests 0 / 15 / 30 / 45 / 60 / 90 deg using {mouldMat.name}
                </span>
              </div>
            )}

            {sweepResults && (
              <div>
                <div className="cte-section-label" style={{ marginBottom: 4 }}>
                  Max Distortion by Build Angle — {mouldMat.name} at {partCureTemp} deg C
                </div>
                <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginBottom: 8 }}>
                  Click a bar to select that build angle for compensation
                </div>
                <div className="cte-sweep-grid">
                  {sweepResults.map(r => {
                    const pct = maxSweepDisp > 0 ? (r.maxDispMM / maxSweepDisp) * 100 : 0;
                    const isBest = r.angle === bestSweepAngle;
                    const isSelected = r.angle === selectedAngle;
                    const hue = 120 - (pct / 100) * 120;
                    return (
                      <div
                        key={r.angle}
                        className="cte-sweep-col"
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setSelectedAngle(r.angle); setBuildAzimuth(0); }}
                      >
                        <div className="cte-sweep-value">{r.maxDispMM.toFixed(4)} mm</div>
                        <div
                          className={`cte-sweep-bar ${isBest ? 'best' : ''}`}
                          style={{
                            height: `${Math.max(4, pct)}%`,
                            background: isSelected ? 'var(--rf-primary)' : `hsl(${hue}, 70%, 45%)`,
                            outline: isSelected ? '2px solid var(--rf-primary)' : isBest ? '2px solid var(--rf-success)' : 'none',
                            outlineOffset: '2px',
                          }}
                        />
                        <div className="cte-sweep-label" style={{ color: isSelected ? 'var(--rf-primary)' : undefined, fontWeight: isSelected ? 600 : undefined }}>
                          {r.angle === 0 ? 'Flat' : r.angle === 90 ? 'On Side' : `${r.angle} deg`}
                        </div>
                        {isSelected && <div style={{ fontSize: 9, color: 'var(--rf-primary)', fontWeight: 700, marginTop: 2 }}>SELECTED</div>}
                        {isBest && !isSelected && <div style={{ fontSize: 9, color: 'var(--rf-success)', marginTop: 2 }}>LOWEST</div>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
                  Lowest distortion: <strong style={{ color: 'var(--rf-success)' }}>
                    {bestSweepAngle === 0 ? 'Flat' : bestSweepAngle === 90 ? 'On Side' : `${bestSweepAngle} deg`}
                  </strong>
                  {selectedAngle !== null && selectedAngle !== bestSweepAngle && (
                    <span> | Using: <strong style={{ color: 'var(--rf-primary)' }}>
                      {selectedAngle === 0 ? 'Flat' : selectedAngle === 90 ? 'On Side' : `${selectedAngle} deg`}
                    </strong></span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── COMPENSATED MODEL: GIZMO + PREVIEW + EXPORT ────────────── */}
      {selectedAngle !== null && positions && compResult && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="cte-card">
              <div className="cte-card-header">
                <span>Build Orientation</span>
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
                  Drag to adjust | Compensation updates live
                </span>
              </div>
              <div className="cte-card-body">
                <BuildAngleGizmo
                  buildAngle={selectedAngle}
                  buildAzimuth={buildAzimuth}
                  onAngleChange={handleGizmoAngleChange}
                  size={positions ? dims : null}
                />
                <div className="cte-angle-controls">
                  <div className="cte-form-group" style={{ flex: 1 }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Build Angle</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="number" min={0} max={90} value={selectedAngle}
                          onChange={e => setSelectedAngle(Math.max(0, Math.min(90, +e.target.value || 0)))}
                          style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                        <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                      </span>
                    </label>
                    <input type="range" min="0" max="90" step="1" value={selectedAngle}
                      onChange={e => setSelectedAngle(+e.target.value)} style={{ width: '100%' }} />
                  </div>
                  <div className="cte-form-group" style={{ flex: 1 }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Rotation</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="number" min={0} max={359} value={buildAzimuth}
                          onChange={e => setBuildAzimuth(Math.max(0, Math.min(359, +e.target.value || 0)))}
                          style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                        <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                      </span>
                    </label>
                    <input type="range" min="0" max="359" step="1" value={buildAzimuth}
                      onChange={e => setBuildAzimuth(+e.target.value)} style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="cte-card">
              <div className="cte-card-header">Compensation Results</div>
              <div className="cte-card-body">
                <div className="cte-stats-grid" style={{ marginBottom: 12 }}>
                  {[
                    { label: 'Max Compensation', value: compResult.maxCompMM.toFixed(4), unit: 'mm' },
                    { label: 'Avg Compensation', value: compResult.avgCompMM.toFixed(4), unit: 'mm' },
                    { label: 'Build Angle', value: compResult.buildAngle === 0 ? 'Flat' : `${compResult.buildAngle} deg`, unit: '' },
                    { label: 'Delta T', value: compResult.deltaT.toFixed(1), unit: 'deg C' },
                  ].map(({ label, value, unit }) => (
                    <div key={label} className="cte-stat-box">
                      <div className="cte-stat-label">{label}</div>
                      <div className="cte-stat-value">{value}</div>
                      <div className="cte-stat-unit">{unit}</div>
                    </div>
                  ))}
                </div>
                <div className="cte-axis-grid">
                  {[
                    { axis: 'X', strain: compResult.strainDiag.x },
                    { axis: 'Y', strain: compResult.strainDiag.y },
                    { axis: 'Z', strain: compResult.strainDiag.z },
                  ].map(({ axis, strain }) => (
                    <div key={axis} className="cte-axis-card">
                      <div className="cte-axis-label">AXIS {axis}</div>
                      <div className="cte-axis-value">{strain.toFixed(2)}</div>
                      <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginTop: 3 }}>um/m</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="cte-card" style={{ marginBottom: 16 }}>
            <div className="cte-card-header">
              <span>Compensated Mould Preview — {compResult.matName}</span>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', textTransform: 'none', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-secondary)' }}>
                  <input type="checkbox" checked={showOriginal} onChange={e => setShowOriginal(e.target.checked)}
                    style={{ width: 12, height: 12 }} />
                  Original wireframe
                </label>
                <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={downloadSTL}>
                  Download Compensated STL
                </button>
                {fileIsStep && rawFileBytes && (
                  <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={downloadSTEP} disabled={stepExporting}>
                    {stepExporting ? 'Exporting STEP...' : 'Download Compensated STEP'}
                  </button>
                )}
              </div>
            </div>
            <div className="cte-card-body">
              {fileError && (
                <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)', marginBottom: 12, padding: '8px 12px', background: 'rgba(var(--rf-danger-rgb), 0.1)', borderRadius: 6 }}>
                  {fileError}
                </div>
              )}
              <div className="cte-info-box" style={{ marginBottom: 12 }}>
                The coloured mesh shows the compensated mould (pre-shrunk).
                The wireframe overlay is the original desired shape for comparison.
                At cure temperature the compensated mould will expand to match the desired geometry.
              </div>
              <STLViewer
                positions={positions}
                strainTensor={compResult.strainTensor}
                maxDisp={compResult.maxCompMM}
                showOriginal={showOriginal}
                orient={compResult.orient}
                defScale={defScale}
              />
              <ColorBar maxVal={compResult.maxCompMM} unit="mm" />
              <div className="cte-timeline-controls" style={{ marginTop: 8 }}>
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', whiteSpace: 'nowrap' }}>Visual scale</span>
                <input type="range" className="cte-timeline-slider" min={1} max={10} value={defScale}
                  onChange={e => setDefScale(+e.target.value)} />
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {defScale === 1 ? 'true scale' : `${defScale}× shown larger`}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── GENERATE AI BUILD PLAN ─────────────────────────────────── */}
      {mouldMatId && (
      <div style={{ marginBottom: 16 }}>
        <button
          className="cte-btn cte-btn-primary"
          onClick={generatePlan}
          disabled={aiLoading || !mouldMat || !partMat}
          style={{ width: '100%', padding: '12px 24px', fontSize: 'var(--rf-font-size-sm)' }}
        >
          {aiLoading ? 'Generating Build Plan...' : aiPlan ? 'Regenerate Build Plan' : 'Generate AI Build Plan'}
        </button>
      </div>
      )}

      {/* ─── AI PLAN OUTPUT ─────────────────────────────────────────── */}
      {aiLoading && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-body">
            <div className="cte-plan-loading">
              <div className="cte-plan-loading-dots"><span /><span /><span /></div>
              Analysing your project and generating a comprehensive build plan...
            </div>
          </div>
        </div>
      )}

      {aiError && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-body">
            <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)' }}>
              Failed to generate build plan: {aiError}
            </div>
          </div>
        </div>
      )}

      {aiPlan && !aiLoading && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                background: 'var(--rf-primary)', color: 'white', padding: '2px 8px',
                borderRadius: 4, fontSize: 'var(--rf-font-size-xs)', fontWeight: 700, letterSpacing: '0.5px',
              }}>AI</span>
              <span>Your Build Plan</span>
            </div>
          </div>
          <div className="cte-card-body">
            <div
              className="cte-plan-content"
              dangerouslySetInnerHTML={{ __html: markdownToHTML(aiPlan) }}
            />
          </div>
        </div>
      )}

      {/* ─── EXPORT PDF REPORT ──────────────────────────────────────── */}
      {aiPlan && !aiLoading && (
        <div style={{ marginBottom: 16 }}>
          <button
            className="cte-btn cte-btn-primary"
            onClick={handleExportPDF}
            style={{ width: '100%', padding: '12px 24px', fontSize: 'var(--rf-font-size-sm)' }}
          >
            Export PDF Report
          </button>
        </div>
      )}
    </div>
  );
}
